import React, { useContext } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Typography from '@material-ui/core/Typography';
import DescriptionIcon from '@material-ui/icons/Description';
import FindInPageIcon from '@material-ui/icons/FindInPage';
import StorageIcon from '@material-ui/icons/Storage';
import {
  ApiFilterOperation,
  EmptyComponent,
  TableCellProps,
  TableCells,
  TableColumn,
  TableColumnAlign,
  TableContext,
  TableFilterCells,
  Tooltip,
  eqFilterParams,
  useStaticListSource,
} from '@eas/common-web';
import { ParticipantCell } from '@modules/es-file/es-file-columns';
import { useUserRoles } from '@modules/role/role-api';
import { useCustomFilterAutocomplete } from '@composite/autocomplete-item/autocomplete-item-with-deactivated-items';
import { FilterSelectCell } from '@composite/filter/filter-cells/filter-select-cell';
import { FilterTextCell } from '@composite/filter/filter-cells/filter-text-cell';
import { Redirect } from '@composite/redirect/redirect';
import { StaticDataContext } from '@components/evidence/static-data-provider/static-data-provider';
import {
  autocompleteColumnMapper,
  autocompleteLabelMapper,
  multipleAutocompleteColumnMapper,
  subjectContactColumnMapper,
} from '@components/form/misc/label-mappers';
import { Decision } from '@models';
import { EvidenceBrowserUrl, Role } from '@enums';
import { useSettlementMethods } from '../dict-settlement-method/dict-settlement-method-api';
import { useJudges } from '../judge/judge-api';

function DecisionSearchMatchIcon({ matches }: { matches?: string[] }) {
  matches = matches ?? [];
  const metadataMatch = matches.includes('METADATA');
  const contentMatch = matches.includes('HIGHLIGHT');
  const Icon =
    contentMatch && metadataMatch
      ? FindInPageIcon
      : contentMatch
      ? DescriptionIcon
      : StorageIcon;
  const color =
    contentMatch && metadataMatch
      ? '#ff9800'
      : contentMatch
      ? '#1976d2'
      : '#607d8b';

  if (!metadataMatch && !contentMatch) {
    return null;
  }

  return (
    <Tooltip
      title={
        contentMatch && metadataMatch ? (
          <FormattedMessage
            id="ES__DECISIONS__SEARCH_MATCH__METADATA_AND_CONTENT"
            defaultMessage="Hledaný řetězec byl nalezen v metadatech i obsahu rozhodnutí"
          />
        ) : contentMatch ? (
          <FormattedMessage
            id="ES__DECISIONS__SEARCH_MATCH__CONTENT"
            defaultMessage="Hledaný řetězec byl nalezen v obsahu rozhodnutí"
          />
        ) : (
          <FormattedMessage
            id="ES__DECISIONS__SEARCH_MATCH__METADATA"
            defaultMessage="Hledaný řetězec byl nalezen v metadatech"
          />
        )
      }
    >
      <Icon
        fontSize="small"
        style={{
          color,
          marginLeft: 6,
          verticalAlign: 'text-bottom',
        }}
      />
    </Tooltip>
  );
}

function DecisionNumberCell(props: TableCellProps<Decision>) {
  const { classes, source } = useContext(TableContext);
  const matches = source.matchedQueries?.find(
    (matchedQuery) => matchedQuery.id === props.rowValue.id
  )?.queries;

  return (
    <Typography className={classes.tableCell}>
      {props.value}
      <DecisionSearchMatchIcon matches={matches} />
    </Typography>
  );
}

export function useColumns(wideFilters = true): TableColumn<Decision>[] {
  const intl = useIntl();
  const { anonymizationRequests, decisionForms, decisionStates } =
    useContext(StaticDataContext);

  const useAnalysts = () =>
    useUserRoles({
      ...eqFilterParams({ field: 'role.id', value: Role.ANALYST }),
      include: ['DEACTIVATED'],
    });
  const useAnonymizationRequests = () =>
    useStaticListSource(anonymizationRequests);
  const useDecisionForms = () => useStaticListSource(decisionForms);

  const useStates = () => useStaticListSource(decisionStates);

  return [
    {
      datakey: 'id',
      name: intl.formatMessage({
        id: 'ES__DECISIONS__TABLE__COLUMN__REDIRECT',
        defaultMessage: 'Přesměrování',
      }),
      NameComponent: EmptyComponent,
      width: 50,
      CellComponent: function Cell(props) {
        return (
          <>
            <Redirect
              url={EvidenceBrowserUrl.DECISIONS_ALL}
              id={props.value}
              showRedirectInCurrent={false}
            />
          </>
        );
      },
      align: TableColumnAlign.LEFT,
      sortable: false,
      filterable: false,
    },
    {
      datakey: 'number',
      sortkey: 'numberSort',
      name: intl.formatMessage({
        id: 'ES__DECISIONS__TABLE__COLUMN__ES_FILE',
        defaultMessage: 'Číslo rozhodnutí',
      }),
      width: 200,
      CellComponent: DecisionNumberCell,
      FilterComponent: FilterTextCell,
      filterOperation: ApiFilterOperation.CONTAINS,
      sortable: true,
      filterable: true,
    },
    {
      sortkey: 'esFile.analystUserRole.name',
      datakey: 'esFile.analystUserRole.label',
      filterDialogSpan: wideFilters ? 2 : undefined,
      filterkey: 'esFile.analystUserRole.id',
      name: intl.formatMessage({
        id: 'ES__DECISIONS__TABLE__COLUMN__ANALYTICS',
        defaultMessage: 'Analytik - Indexace',
      }),
      width: 200,
      CellComponent: TableCells.TextCell,
      filterOperation: ApiFilterOperation.EQ,
      FilterComponent: useCustomFilterAutocomplete({
        useSource: useAnalysts,
      }),
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'form',
      sortkey: 'form.name',
      filterkey: 'form.id',
      name: intl.formatMessage({
        id: 'ES__DECISIONS__TABLE__COLUMN__FORM',
        defaultMessage: 'Forma rozhodnutí',
      }),
      width: 200,
      CellComponent: TableCells.TextCell,
      valueMapper: TableCells.useSelectCellFactory(useDecisionForms),
      FilterComponent:
        TableFilterCells.useFilterSelectCellFactory(useDecisionForms),
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'decisionDate',
      name: intl.formatMessage({
        id: 'ES__DECISIONS__TABLE__COLUMN__DECISION_DATE',
        defaultMessage: 'Datum rozhodnutí',
      }),
      width: 200,
      CellComponent: TableCells.DateCell,
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'esFile.anonymizationRequest',
      sortkey: 'esFile.anonymizationRequest.name',
      filterkey: 'esFile.anonymizationRequest.id',
      name: intl.formatMessage({
        id: 'ES__DECISIONS__TABLE__COLUMN__ANONYMIZATION_REQUEST',
        defaultMessage: 'Žádost o anonymizaci',
      }),
      width: 200,
      CellComponent: TableCells.TextCell,
      valueMapper: TableCells.useSelectCellFactory(useAnonymizationRequests),
      FilterComponent: TableFilterCells.useFilterSelectCellFactory(
        useAnonymizationRequests
      ),
      filterOperation: ApiFilterOperation.EQ,
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'settlementMethods',
      sortkey: 'settlementMethods.concatenated',
      filterkey: 'settlementMethods.id',
      name: intl.formatMessage({
        id: 'ES__DECISIONS__TABLE__COLUMN__SETTLEMENT_METHODS',
        defaultMessage: 'Typ výroku',
      }),
      width: 200,
      CellComponent: TableCells.TextCell,
      valueMapper: multipleAutocompleteColumnMapper,
      FilterComponent: TableFilterCells.useFilterAutocompleteCellFactory(
        useSettlementMethods,
        autocompleteLabelMapper
      ),
      sortable: true,
      filterable: true,
    },
    {
      datakey: 'judge',
      filterDialogSpan: wideFilters ? 2 : undefined,
      filterkey: 'judge.id',
      sortkey: 'judge.name',
      name: intl.formatMessage({
        id: 'ES__DECISIONS__TABLE__COLUMN__JUDGE',
        defaultMessage: 'Soudce zpravodaj',
      }),
      width: 200,
      CellComponent: TableCells.TextCell,
      valueMapper: autocompleteColumnMapper,
      FilterComponent: useCustomFilterAutocomplete({
        useSource: () => useJudges({ include: ['DEACTIVATED'] }),
      }),
      filterOperation: ApiFilterOperation.EQ,
      sortable: true,
      filterable: true,
      filterGroup: 1,
    },
    {
      datakey: 'esFile.proposers',
      filterDialogSpan: wideFilters ? 2 : undefined,
      filterkey: 'esFile.proposers.contact.subject.name',
      sortkey: 'esFile.proposers.contact.name',
      name: intl.formatMessage({
        id: 'ES__DECISIONS__TABLE__COLUMN__PROPOSERS',
        defaultMessage: 'Navrhovatelé',
      }),
      width: 300,
      CellComponent: ParticipantCell('esFile.proposers' as any),
      valueMapper: subjectContactColumnMapper,
      FilterComponent: FilterTextCell,
      filterOperation: ApiFilterOperation.CONTAINS,
      sortable: true,
      filterable: true,
      filterGroup: 1,
    },
    {
      datakey: 'updated',
      filterkey: 'updated',
      sortkey: 'updated',
      name: intl.formatMessage({
        id: 'ES__DECISIONS__TABLE__COLUMN__UPDATED',
        defaultMessage: 'Datum změny',
      }),
      width: 300,
      CellComponent: TableCells.DateTimeCell,
      FilterComponent: TableFilterCells.FilterDateTimeCell,
      sortable: true,
      filterable: true,
      filterGroup: 1,
    },
    {
      datakey: 'state',
      filterkey: 'state.id',
      sortkey: 'state.name',
      name: intl.formatMessage({
        id: 'ES__DECISIONS__TABLE__COLUMN__STATE',
        defaultMessage: 'Stav',
      }),
      width: 200,
      CellComponent: TableCells.TextCell,
      FilterComponent: FilterSelectCell(useStates),
      valueMapper: TableCells.useSelectCellFactory(useStates),
      filterOperation: ApiFilterOperation.EQ,
      filterable: true,
      visible: true,
      filterGroup: 1,
    },
    // {
    //   datakey: 'text.introduction',
    //   name: intl.formatMessage({
    //     id: 'ES__DECISIONS__TABLE__COLUMN__INTRODUCTION',
    //     defaultMessage: 'Návětí - plné znění',
    //   }),
    //   width: 200,
    //   CellComponent: TableCells.TextCell,
    //   filterable: true,
    //   visible: false,
    //   enableHighlighting: true,
    //   filterGroup: 3,
    // },
    // {
    //   datakey: 'text.statement',
    //   name: intl.formatMessage({
    //     id: 'ES__DECISIONS__TABLE__COLUMN__STATEMENT',
    //     defaultMessage: 'Výrok - plné znění',
    //   }),
    //   width: 200,
    //   CellComponent: TableCells.TextCell,
    //   filterable: true,
    //   visible: false,
    //   enableHighlighting: true,
    //   filterGroup: 3,
    // },
    // {
    //   datakey: 'text.narration',
    //   name: intl.formatMessage({
    //     id: 'ES__DECISIONS__TABLE__COLUMN__NARRATION',
    //     defaultMessage: 'Narace - plné znění',
    //   }),
    //   width: 200,
    //   CellComponent: TableCells.TextCell,
    //   filterable: true,
    //   visible: false,
    //   enableHighlighting: true,
    //   filterGroup: 3,
    // },
    // {
    //   datakey: 'text.argumentation',
    //   name: intl.formatMessage({
    //     id: 'ES__DECISIONS__TABLE__COLUMN__ARGUMENTATION',
    //     defaultMessage: 'Argumentace - plné znění',
    //   }),
    //   width: 200,
    //   CellComponent: TableCells.TextCell,
    //   filterable: true,
    //   visible: false,
    //   enableHighlighting: true,
    //   filterGroup: 3,
    // },
    // {
    //   datakey: 'text.legalSentence',
    //   name: intl.formatMessage({
    //     id: 'ES__DECISIONS__TABLE__COLUMN__LEGAL_SENTENCE',
    //     defaultMessage: 'Právní věta - plné znění',
    //   }),
    //   width: 200,
    //   CellComponent: TableCells.TextCell,
    //   filterable: true,
    //   visible: false,
    //   enableHighlighting: true,
    //   filterGroup: 3,
    // },
    // {
    //   datakey: 'text.abstract',
    //   name: intl.formatMessage({
    //     id: 'ES__DECISIONS__TABLE__COLUMN__ABSTRACT',
    //     defaultMessage: 'Abstrakt - plné znění',
    //   }),
    //   width: 200,
    //   CellComponent: TableCells.TextCell,
    //   filterable: true,
    //   visible: false,
    //   enableHighlighting: true,
    //   filterGroup: 3,
    // },
    // {
    //   datakey: 'text.differentOpinion',
    //   name: intl.formatMessage({
    //     id: 'ES__DECISIONS__TABLE__COLUMN__DIFFERENT_OPINION',
    //     defaultMessage: 'Odlišné stanovisko - plné znění',
    //   }),
    //   width: 200,
    //   CellComponent: TableCells.TextCell,
    //   filterable: true,
    //   visible: false,
    //   enableHighlighting: true,
    //   filterGroup: 3,
    // },
    // {
    //   datakey: 'anonymizedText.introduction',
    //   name: intl.formatMessage({
    //     id: 'ES__DECISIONS__TABLE__COLUMN__INTRODUCTION',
    //     defaultMessage: 'Návětí - anonymizováno',
    //   }),
    //   width: 200,
    //   CellComponent: TableCells.TextCell,
    //   filterable: true,
    //   visible: false,
    //   enableHighlighting: true,
    //   filterGroup: 4,
    // },
    // {
    //   datakey: 'anonymizedText.statement',
    //   name: intl.formatMessage({
    //     id: 'ES__DECISIONS__TABLE__COLUMN__STATEMENT',
    //     defaultMessage: 'Výrok - anonymizováno',
    //   }),
    //   width: 200,
    //   CellComponent: TableCells.TextCell,
    //   filterable: true,
    //   visible: false,
    //   enableHighlighting: true,
    //   filterGroup: 4,
    // },
    // {
    //   datakey: 'anonymizedText.narration',
    //   name: intl.formatMessage({
    //     id: 'ES__DECISIONS__TABLE__COLUMN__NARRATION',
    //     defaultMessage: 'Narace - anonymizováno',
    //   }),
    //   width: 200,
    //   CellComponent: TableCells.TextCell,
    //   filterable: true,
    //   visible: false,
    //   enableHighlighting: true,
    //   filterGroup: 4,
    // },
    // {
    //   datakey: 'anonymizedText.argumentation',
    //   name: intl.formatMessage({
    //     id: 'ES__DECISIONS__TABLE__COLUMN__ARGUMENTATION',
    //     defaultMessage: 'Argumentace - anonymizováno',
    //   }),
    //   width: 200,
    //   CellComponent: TableCells.TextCell,
    //   filterable: true,
    //   visible: false,
    //   enableHighlighting: true,
    //   filterGroup: 4,
    // },
    // {
    //   datakey: 'anonymizedText.legalSentence',
    //   name: intl.formatMessage({
    //     id: 'ES__DECISIONS__TABLE__COLUMN__LEGAL_SENTENCE',
    //     defaultMessage: 'Právní věta - anonymizováno',
    //   }),
    //   width: 200,
    //   CellComponent: TableCells.TextCell,
    //   filterable: true,
    //   visible: false,
    //   enableHighlighting: true,
    //   filterGroup: 4,
    // },
    // {
    //   datakey: 'anonymizedText.abstract',
    //   name: intl.formatMessage({
    //     id: 'ES__DECISIONS__TABLE__COLUMN__ABSTRACT',
    //     defaultMessage: 'Abstrakt - anonymizováno',
    //   }),
    //   width: 200,
    //   CellComponent: TableCells.TextCell,
    //   filterable: true,
    //   visible: false,
    //   enableHighlighting: true,
    //   filterGroup: 4,
    // },
    // {
    //   datakey: 'anonymizedText.differentOpinion',
    //   name: intl.formatMessage({
    //     id: 'ES__DECISIONS__TABLE__COLUMN__DIFFERENT_OPINION',
    //     defaultMessage: 'Odlišné stanovisko - anonym.',
    //   }),
    //   width: 200,
    //   CellComponent: TableCells.TextCell,
    //   filterable: true,
    //   visible: false,
    //   enableHighlighting: true,
    //   filterGroup: 4,
    // },
  ];
}
